import React, { useState, useEffect } from 'react';
import { DropDownComponent, InputComponent, FileUploadComponent } from '../custcomponents';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { AxiosAdminInstance } from '../utils';

interface Partner {
    id: string;
    name: string;
    website: string;
    category: string;
    imageurl: string;
    priority: number;
}

export const ManagePartners = () => {
    const { eventcode } = useParams<{ eventcode: string }>();
    const [partners, setPartners] = useState<Partner[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
    const [newPartner, setNewPartner] = useState({
        name: '',
        website: '',
        category: '',
        priority: 0,
        imageFile: null as File | null,
        imageurl: '',
        contentType: ''
    });

    useEffect(() => {
        fetchPartners();
    }, []);

    useEffect(() => {
        if (editingPartner) {
            setNewPartner({
                name: editingPartner.name || '',
                website: editingPartner.website || '',
                category: editingPartner.category || '',
                priority: editingPartner.priority || 0,
                imageFile: null,
                imageurl: editingPartner.imageurl || '',
                contentType: ''
            });
        } else {
            setNewPartner({
                name: '',
                website: '',
                category: '',
                priority: 0,
                imageFile: null,
                imageurl: '',
                contentType: ''
            });
        }
    }, [editingPartner]);

    const fetchPartners = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/managePartners', {
                action: 'getPartners',
                data: { eventcode }
            });
            if (response.data.success) {
                const partnersObj = response.data.partners || {};
                const partnersArray = Object.keys(partnersObj).map(id => ({
                    id,
                    ...partnersObj[id]
                }));
                partnersArray.sort((a, b) => b.priority - a.priority);
                setPartners(partnersArray);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to fetch partners');
        }
        setLoading(false);
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result as string;
                // Remove the data:image/jpeg;base64, prefix
                const base64Data = base64.split(',')[1];
                resolve(base64Data);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleAddPartner = async () => {
        if (!newPartner.name.trim()) {
            toast.error('Partner name is required');
            return;
        }
        if (!newPartner.category) {
            toast.error('Category is required');
            return;
        }
        setLoading(true);
        try {
            let data = {
                eventcode,
                name: newPartner.name,
                website: newPartner.website,
                category: newPartner.category,
                priority: newPartner.priority,
                imageurl: newPartner.imageurl
            };
            if (editingPartner) {
                data = { ...data, partnerId: editingPartner.id } as any;
            }
            if (newPartner.imageFile) {
                const imageBase64 = await fileToBase64(newPartner.imageFile);
                data = { ...data, imageBase64, imageFileName: newPartner.imageFile.name, contentType: newPartner.contentType } as any;
            }
            const response = await AxiosAdminInstance.post('/api/admin/managePartners', {
                action: editingPartner ? 'editPartner' : 'addPartner',
                data
            });
            if (response.data.success) {
                toast.success(editingPartner ? 'Partner updated successfully' : 'Partner added successfully');
                setNewPartner({
                    name: '',
                    website: '',
                    category: '',
                    priority: 0,
                    imageFile: null,
                    imageurl: '',
                    contentType: ''
                });
                setEditingPartner(null);
                fetchPartners();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(editingPartner ? 'Failed to update partner' : 'Failed to add partner');
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setNewPartner({
            name: '',
            website: '',
            category: '',
            priority: 0,
            imageFile: null,
            imageurl: '',
            contentType: ''
        });
        setEditingPartner(null);
    };

    const handleDeletePartner = async (partnerId: string) => {
        if (!window.confirm('Are you sure you want to delete this partner?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/managePartners', {
                action: 'deletePartner',
                data: { eventcode, partnerId }
            });
            if (response.data.success) {
                toast.success('Partner deleted successfully');
                fetchPartners();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete partner');
        }
        setLoading(false);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return false;
            }
            // Validate file size (100KB max)
            if (file.size > 100 * 1024) {
                toast.error('File size must be less than 100KB');
                return false;
            }
            setNewPartner(prev => ({ ...prev, imageFile: file, imageurl: '', contentType: file.type }));
            return true;
        }
        return false;
    };

    const partnerColumns = [
        { name: 'Actions', cell: (row: Partner) => (
            <div>
                <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingPartner(row)} title="Edit">
                    <i className="fa fa-edit"></i>
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDeletePartner(row.id)} title="Delete">
                    <i className="fa fa-trash"></i>
                </button>
            </div>
        ) },
        { name: 'Name', selector: (row: Partner) => row.name, sortable: true },
        { 
            name: 'Website', 
            cell: (row: Partner) => (
                row.website ? (
                    <a href={row.website} target="_blank" rel="noopener noreferrer" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                        {row.website}
                    </a>
                ) : 'No Website'
            ), 
            sortable: true 
        },
        { name: 'Category', selector: (row: Partner) => row.category, sortable: true },
        { name: 'Priority', selector: (row: Partner) => row.priority, sortable: true },
        { name: 'Image', cell: (row: Partner) => (
            row.imageurl ? <img src={row.imageurl} alt={row.name} style={{ width: '50px', height: '50px', objectFit: 'cover' }} /> : 'No Image'
        ) }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-handshake-o fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage Partners</h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); }} >
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingPartner ? 'Edit Partner' : 'Add New Partner'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="name"
                                            custplaceholder="Partner Name"
                                            custid="partnerName"
                                            onExternalValue={(val: string) => setNewPartner(prev => ({ ...prev, name: val }))}
                                            onExternalBind={newPartner.name}
                                        />
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="website"
                                            custplaceholder="Website"
                                            custid="partnerWebsite"
                                            onExternalValue={(val: string) => setNewPartner(prev => ({ ...prev, website: val }))}
                                            onExternalBind={newPartner.website}
                                        />
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="category"
                                            custplaceholder="Category"
                                            custid="partnerCategory"
                                            onExternalValue={(val: string) => setNewPartner(prev => ({ ...prev, category: val }))}
                                            onExternalBind={newPartner.category}
                                        />
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="priority"
                                            custplaceholder="Priority (number)"
                                            custid="partnerPriority"
                                            onExternalValue={(val: string) => setNewPartner(prev => ({ ...prev, priority: parseInt(val) || 0 }))}
                                            onExternalBind={newPartner.priority?.toString()}
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <FileUploadComponent
                                            custname="image"
                                            custplaceholder="Upload Image"
                                            custid="partnerImage"
                                            onChange={handleFileChange}
                                        />
                                        {newPartner.imageurl && (
                                            <div className="mt-2">
                                                <img src={newPartner.imageurl} alt="Current" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn btn-primary" type="button" onClick={handleAddPartner} disabled={loading}>
                                            {editingPartner ? 'Update Partner' : 'Add Partner'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        {editingPartner && (
                                            <button className="btn btn-secondary ms-2" type="button" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Partners</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={partnerColumns}
                                            data={partners}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};